import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getSleepConfig, updateSleepConfig, getSleepLogs, createSleepLog, extractErrorMessage } from '../services/api';
import { FaPlus, FaBed, FaClipboardList, FaSave } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

function SleepConfig({ user }) {
  const toast = useToast();
  const [savingLog, setSavingLog] = useState(false);
  const [formErrorLog, setFormErrorLog] = useState(null);
  const [config, setConfig] = useState({
    bedtime: '',
    wake_time: '',
    min_hours: 6,
    max_hours: 9,
    rules: ''
  });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [newLog, setNewLog] = useState({
    user_id: '',
    slept_at: '',
    woke_at: '',
    duration_hours: '',
    quality: 5,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [configData, logsData] = await Promise.all([
        getSleepConfig(),
        getSleepLogs()
      ]);
      if (configData) {
        setConfig({
          bedtime: configData.bedtime || '',
          wake_time: configData.wake_time || '',
          min_hours: configData.min_hours || 6,
          max_hours: configData.max_hours || 9,
          rules: configData.rules || ''
        });
      }
      setLogs(logsData.logs || logsData);
    } catch (error) {
      console.error('Error fetching sleep data:', error);
      toast.error(extractErrorMessage(error, 'Schlaf-Daten konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await updateSleepConfig(config);
      toast.success('Schlaf-Konfiguration gespeichert');
    } catch (error) {
      console.error('Error saving sleep config:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Speichern der Schlaf-Konfiguration'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateLog = async (e) => {
    e.preventDefault();
    if (savingLog) return;
    setSavingLog(true);
    setFormErrorLog(null);
    try {
      await createSleepLog(newLog);
      setNewLog({ user_id: '', slept_at: '', woke_at: '', duration_hours: '', quality: 5, notes: '' });
      setShowLogModal(false);
      toast.success('Schlaf-Eintrag gespeichert');
      fetchData();
    } catch (error) {
      console.error('Error creating sleep log:', error);
      setFormErrorLog(extractErrorMessage(error, 'Fehler beim Erstellen des Schlaf-Eintrags'));
    } finally {
      setSavingLog(false);
    }
  };

  const closeLogModal = () => {
    if (savingLog) return;
    setShowLogModal(false);
    setFormErrorLog(null);
  };

  const getQualityLabel = (quality) => {
    if (quality >= 8) return 'Excellent';
    if (quality >= 6) return 'Good';
    if (quality >= 4) return 'Fair';
    if (quality >= 2) return 'Poor';
    return 'Terrible';
  };

  const getQualityColor = (quality) => {
    if (quality >= 8) return '#43b581';
    if (quality >= 6) return '#7289da';
    if (quality >= 4) return '#faa61a';
    return '#f04747';
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        {/* Sleep Config Section */}
        <div className="page-header">
          <h1><FaBed /> Sleep Configuration</h1>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <div className="data-table-container" style={{ padding: '1.5rem' }}>
              <form onSubmit={handleSaveConfig}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Bedtime</label>
                    <input
                      type="time"
                      value={config.bedtime}
                      onChange={e => setConfig({...config, bedtime: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Wake Time</label>
                    <input
                      type="time"
                      value={config.wake_time}
                      onChange={e => setConfig({...config, wake_time: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Minimum Hours</label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      step="0.5"
                      value={config.min_hours}
                      onChange={e => setConfig({...config, min_hours: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Maximum Hours</label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      step="0.5"
                      value={config.max_hours}
                      onChange={e => setConfig({...config, max_hours: parseFloat(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Rules</label>
                  <textarea
                    value={config.rules}
                    onChange={e => setConfig({...config, rules: e.target.value})}
                    placeholder="Sleep rules and requirements..."
                    rows={4}
                  />
                </div>
                <div className="modal-actions">
                  <button type="submit" className="submit-btn" disabled={saving}>
                    <FaSave /> {saving ? 'Saving...' : 'Save Config'}
                  </button>
                </div>
              </form>
            </div>

            {/* Sleep Logs Section */}
            <div className="page-header" style={{ marginTop: '2rem' }}>
              <h2><FaClipboardList /> Sleep Logs</h2>
              <button className="add-btn" onClick={() => setShowLogModal(true)}>
                <FaPlus /> New Sleep Log
              </button>
            </div>

            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>User</th>
                    <th>Slept At</th>
                    <th>Woke At</th>
                    <th>Duration</th>
                    <th>Quality</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length > 0 ? (
                    logs.map(log => (
                      <tr key={log.id}>
                        <td>
                          {new Date(log.created_at).toLocaleDateString('de-DE', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </td>
                        <td>{log.user_id || '-'}</td>
                        <td>{log.slept_at || '-'}</td>
                        <td>{log.woke_at || '-'}</td>
                        <td>{log.duration_hours ? `${log.duration_hours}h` : '-'}</td>
                        <td>
                          <span style={{ color: getQualityColor(log.quality), fontWeight: 'bold' }}>
                            {log.quality}/10 ({getQualityLabel(log.quality)})
                          </span>
                        </td>
                        <td>{log.notes || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="no-data">No sleep logs recorded</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* New Sleep Log Modal */}
        {showLogModal && (
          <div className="modal-overlay" onClick={closeLogModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>New Sleep Log</h2>
              {formErrorLog && <div className="shared-form-error-banner">{formErrorLog}</div>}
              <form onSubmit={handleCreateLog}>
                <div className="form-group">
                  <UserSelect
                    value={newLog.user_id}
                    onChange={id => setNewLog({...newLog, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Slept At</label>
                  <input
                    type="datetime-local"
                    value={newLog.slept_at}
                    onChange={e => setNewLog({...newLog, slept_at: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Woke At</label>
                  <input
                    type="datetime-local"
                    value={newLog.woke_at}
                    onChange={e => setNewLog({...newLog, woke_at: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Duration (hours)</label>
                  <input
                    type="number"
                    min="0"
                    max="24"
                    step="0.25"
                    value={newLog.duration_hours}
                    onChange={e => setNewLog({...newLog, duration_hours: e.target.value})}
                    placeholder="Auto-calculated if left empty"
                  />
                </div>
                <div className="form-group">
                  <label>Quality: {newLog.quality}/10 ({getQualityLabel(newLog.quality)})</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newLog.quality}
                    onChange={e => setNewLog({...newLog, quality: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={newLog.notes}
                    onChange={e => setNewLog({...newLog, notes: e.target.value})}
                    placeholder="How did you sleep?"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeLogModal} disabled={savingLog}>Cancel</button>
                  <button type="submit" className="submit-btn" disabled={savingLog}>{savingLog ? 'Speichert…' : 'Log Sleep'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default SleepConfig;
